import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    IconButton,
    Avatar,
    Stack,
    Divider,
    LinearProgress,
    Paper,
    Select,
    MenuItem,
    FormControl,
    Button,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Zoom,
    Fab,
    TextField
} from '@mui/material';
import {
    LiveTv as LiveIcon,
    Refresh as RefreshIcon,
    Business as BusinessIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HourglassEmpty as PendingIcon,
    ChevronRight as ChevronRightIcon,
    EventAvailable as EventIcon,
    FilterList as FilterIcon,
    History as HistoryIcon,
    Add as AddIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Notes as NotesIcon
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, isToday, isYesterday, isTomorrow } from 'date-fns';
import { es } from 'date-fns/locale';
import CatLoadingSpinner from '../components/CatLoadingSpinner';
import { useSocket } from '../contexts/SocketContext';
import { useCatAssistant } from '../contexts/CatAssistantContext';
import ReservationForm, { ReservationFormData } from '../components/ReservationForm';

interface Reservation {
    id: number;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    startDate: string;
    endDate: string;
    status: 'pending' | 'confirmed' | 'cancelled';
    notes?: string;
    board?: {
        id: number;
        name: string;
        business?: {
            id: number;
            name: string;
        };
    };
}

const LiveDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { mode, variant } = useThemeMode();
    const isDark = mode === 'dark';
    const isBlack = variant === 'pure-black' && isDark;
    const isWhite = variant === 'pure-white' && !isDark;
    const isTrippy = variant === 'trippy';

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [filterBusiness, setFilterBusiness] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [activeTab, setActiveTab] = useState(2); // 0: Ayer, 1: Pendientes, 2: Hoy, 3: Mañana
    const [selectedRes, setSelectedRes] = useState<Reservation | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [boards, setBoards] = useState<any[]>([]);

    const getBgColor = () => {
        if (isTrippy) return isDark ? '#1A0836' : '#FF00FF10';
        if (isBlack) return '#000000';
        if (isWhite) return '#FFFFFF';
        return isDark ? '#0A0A0A' : '#F5F8FA';
    };

    const getCardBg = () => {
        if (isTrippy) return isDark ? 'rgba(26, 8, 54, 0.6)' : 'rgba(255, 255, 255, 0.7)';
        if (isBlack) return 'rgba(15, 15, 15, 0.8)';
        return isDark ? 'rgba(30, 41, 59, 0.4)' : '#FFFFFF';
    };

    const socket = useSocket();
    const { say } = useCatAssistant();

    const fetchLiveData = useCallback(async () => {
        try {
            const response = await axios.get('/api/dashboard/live');
            setData(response.data);

            // Extract boards for editing
            if (response.data.boards) {
                setBoards(response.data.boards.map((b: any) => ({
                    id: b.id.toString(),
                    name: b.name,
                    type: b.type
                })));
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching live data:', error);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLiveData();

        if (!socket) return;

        const handleCreated = () => {
            console.log('Real-time: New reservation created');
            fetchLiveData();
        };

        const handleUpdated = () => {
            console.log('Real-time: Reservation updated');
            fetchLiveData();
        };

        const handleDeleted = () => {
            console.log('Real-time: Reservation deleted');
            fetchLiveData();
        };

        socket.on('reservationCreated', handleCreated);
        socket.on('reservationUpdated', handleUpdated);
        socket.on('reservationDeleted', handleDeleted);

        return () => {
            socket.off('reservationCreated', handleCreated);
            socket.off('reservationUpdated', handleUpdated);
            socket.off('reservationDeleted', handleDeleted);
        };
    }, [fetchLiveData, socket]);

    if (loading) return <CatLoadingSpinner message="Sincronizando con la red de turnos..." />;

    const { reservations = [], businesses = [], counts = {} } = data || {};

    const filteredReservations = reservations.filter((res: Reservation) => {
        const matchesBusiness = filterBusiness === 'all' || res.board?.business?.id.toString() === filterBusiness;
        const matchesStatus = filterStatus === 'all' || res.status === filterStatus;
        const matchesSearch = !searchTerm || 
            res.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            res.customerPhone?.includes(searchTerm);

        const date = new Date(res.startDate);

        if (activeTab === 0) return matchesBusiness && matchesStatus && matchesSearch && isYesterday(date);
        if (activeTab === 1) return matchesBusiness && matchesStatus && matchesSearch && res.status === 'pending';
        if (activeTab === 2) return matchesBusiness && matchesStatus && matchesSearch && isToday(date);
        if (activeTab === 3) return matchesBusiness && matchesStatus && matchesSearch && isTomorrow(date);

        return matchesBusiness && matchesStatus && matchesSearch;
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'confirmed': return '#10b981';
            case 'pending': return '#f59e0b';
            case 'cancelled': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const handleEditSubmit = async (formData: ReservationFormData) => {
        if (!selectedRes) return;
        try {
            await axios.put(`/api/reservations/${selectedRes.id}`, formData);
            setLoading(true);
            await fetchLiveData();
            setIsEditing(false);
            setSelectedRes(null);
        } catch (error) {
            console.error('Error updating reservation:', error);
            alert('Error al actualizar la reserva');
        }
    };

    const getStatusBg = (status: string) => {
        const color = getStatusColor(status);
        return isDark ? `${color}20` : `${color}10`;
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'confirmed': return 'Confirmado';
            case 'pending': return 'Pendiente';
            case 'cancelled': return 'Cancelado';
            default: return status;
        }
    };

    return (
        <Box className={isTrippy ? 'theme-trippy' : ''} sx={{ minHeight: '100vh', bgcolor: getBgColor(), pt: { xs: 2, md: 4 }, pb: 8, transition: 'all 0.5s ease' }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                {/* Header */}
                <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 3 }}>
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, boxShadow: '0 0 20px rgba(99, 102, 241, 0.4)' }}>
                                <LiveIcon fontSize="large" />
                            </Avatar>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-1.5px', color: isDark ? '#fff' : '#1e293b', fontSize: { xs: '1.8rem', md: '2.8rem' } }}>
                                    Panel en Vivo 🔴
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 800, fontSize: { xs: '0.8rem', md: '1rem' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component="span" sx={{ width: 10, height: 10, bgcolor: '#ff4b4b', borderRadius: '50%', display: 'inline-block', animation: 'pulse 2s infinite' }} />
                                    Gestionando todas tus agendas (Actualizado)
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/nuevoturno')}
                            sx={{ borderRadius: 3, fontWeight: 800, px: 3, textTransform: 'none' }}
                        >
                            Nuevo
                        </Button>
                        <IconButton onClick={() => { setLoading(true); fetchLiveData(); }} sx={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 3, color: isDark ? 'white' : 'inherit' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Stack>
                </Box>

                {/* Metrics */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {[
                        { label: 'Ayer', value: counts.totalYesterday, icon: <HistoryIcon />, color: '#64748b' },
                        { label: 'Hoy', value: counts.totalToday, icon: <EventIcon />, color: '#6366f1' },
                        { label: 'Pendientes', value: counts.totalPending, icon: <PendingIcon />, color: '#f59e0b' },
                        { label: 'Mañana', value: counts.totalTomorrow, icon: <ChevronRightIcon />, color: '#10b981' }
                    ].map((stat, idx) => (
                        <Grid size={{ xs: 6, md: 3 }} key={idx}>
                            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: getCardBg(), backdropFilter: 'blur(10px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" textAlign={{ xs: 'center', sm: 'left' }}>
                                    <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 } }}>{stat.icon}</Avatar>
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 900, fontSize: { xs: '1.2rem', md: '2rem' } }}>{stat.value}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.65rem' }}>{stat.label}</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Filters & Tabs */}
                <Paper sx={{ p: 1.5, mb: 4, borderRadius: 4, bgcolor: getCardBg(), display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'center', gap: 2, backdropFilter: 'blur(10px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ width: { xs: '100%', lg: 'auto' }, minHeight: 48, '& .MuiTab-root': { fontWeight: 900, borderRadius: 3, mx: 0.5, px: { xs: 2, md: 3 }, fontSize: { xs: '0.75rem', md: '0.875rem' }, minWidth: 100 } }}
                    >
                        <Tab label="Ayer" />
                        <Tab label="Pendientes" />
                        <Tab label="Hoy" />
                        <Tab label="Mañana" />
                    </Tabs>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' } }} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%', flex: 1, px: 1 }}>
                        <TextField 
                            size="small" 
                            placeholder="Buscar cliente..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ flex: 2, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                            InputProps={{ startAdornment: <FilterIcon sx={{ mr: 1, opacity: 0.5, fontSize: '1rem' }} /> }}
                        />
                        <FormControl size="small" sx={{ flex: 1.5 }}>
                            <Select value={filterBusiness} onChange={(e) => setFilterBusiness(e.target.value)} sx={{ borderRadius: 3, color: isDark ? 'white' : 'inherit', fontWeight: 700 }}>
                                <MenuItem value="all">Todos los Negocios</MenuItem>
                                {businesses.map((b: any) => <MenuItem key={b.id} value={b.id.toString()}>{b.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ borderRadius: 3, color: isDark ? 'white' : 'inherit', fontWeight: 700 }}>
                                <MenuItem value="all">Cualquier Estado</MenuItem>
                                <MenuItem value="confirmed">Confirmados</MenuItem>
                                <MenuItem value="pending">Pendientes</MenuItem>
                                <MenuItem value="cancelled">Cancelados</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Paper>

                {/* Feed */}
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, lg: 8 }}>
                        <Stack spacing={2}>
                            {filteredReservations.length > 0 ? filteredReservations.map((res: Reservation) => (
                                <Paper key={res.id} onClick={() => setSelectedRes(res)} sx={{
                                    p: 2, borderRadius: 4, cursor: 'pointer', transition: 'all 0.2s ease',
                                    bgcolor: getCardBg(), backdropFilter: 'blur(10px)',
                                    '&:hover': { transform: 'scale(1.01)', bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.02)' },
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                                    position: 'relative', overflow: 'hidden',
                                    '&:before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: getStatusColor(res.status) }
                                }}>
                                    <Grid container alignItems="center" spacing={1}>
                                        <Grid size={{ xs: 3, sm: 2 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: { xs: '0.8rem', md: '1.1rem' }, color: isDark ? 'white' : 'inherit', mb: -0.5 }}>{format(new Date(res.startDate), 'HH:mm')}</Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, fontSize: '0.65rem', display: 'block' }}>{format(new Date(res.startDate), 'dd MMM')}</Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.6rem' }}>{format(new Date(res.endDate), 'HH:mm')}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6, sm: 5 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: { xs: '0.85rem', md: '1rem' }, color: isDark ? 'white' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.customerName}</Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.board?.business?.name}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3, sm: 5 }} sx={{ textAlign: 'right' }}>
                                            <Chip label={getStatusLabel(res.status)} size="small" sx={{ fontWeight: 900, bgcolor: getStatusBg(res.status), color: getStatusColor(res.status), borderRadius: 1.5, fontSize: '0.65rem', height: 20 }} />
                                            <IconButton size="small" sx={{ ml: 1, display: { xs: 'none', sm: 'inline-flex' } }}><ChevronRightIcon /></IconButton>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            )) : (
                                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 5, border: '2px dashed rgba(0,0,0,0.05)' }}>
                                    <Typography color="text.secondary">No hay turnos registrados para este criterio.</Typography>
                                </Paper>
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 4 }}>
                        {/* Saturation Side Panel */}
                        <Paper sx={{ p: 4, borderRadius: 5, bgcolor: getCardBg(), backdropFilter: 'blur(10px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 3, color: isDark ? 'white' : 'inherit' }}>Saturación de Hoy</Typography>
                            <Stack spacing={4}>
                                {[
                                    { name: 'Ocupación', val: counts.totalToday, max: 50, color: '#6366f1' },
                                    { name: 'Confirmación', val: counts.confirmedToday, max: counts.totalToday || 1, color: '#10b981' },
                                    { name: 'Pendientes', val: counts.totalPending, max: counts.totalPending || 1, color: '#f59e0b' }
                                ].map((bar, i) => (
                                    <Box key={i}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 800 }}>{bar.name}</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 900 }}>{Math.round((bar.val / bar.max) * 100)}%</Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={Math.min((bar.val / bar.max) * 100, 100)} sx={{ height: 10, borderRadius: 5, bgcolor: 'rgba(0,0,0,0.05)', '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: bar.color } }} />
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* Fab for mobile */}
            <Zoom in>
                <Fab color="primary" onClick={() => navigate('/nuevoturno')} sx={{ position: 'fixed', bottom: 32, right: 32, display: { xs: 'flex', md: 'none' } }}>
                    <AddIcon />
                </Fab>
            </Zoom>

            {/* Detail Modal */}
            <Dialog open={!!selectedRes} onClose={() => setSelectedRes(null)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 5, p: 1 } }}>
                {selectedRes && (
                    <>
                        <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem', pb: 1 }}>Detalle del Turno</DialogTitle>
                        <DialogContent>
                            <Stack spacing={3} sx={{ mt: 1 }}>
                                <Box sx={{ p: 2, bgcolor: getStatusBg(selectedRes.status), borderRadius: 3, border: `1px solid ${getStatusColor(selectedRes.status)}40` }}>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: getStatusColor(selectedRes.status) }}>{selectedRes.customerName}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>ID de Reserva: #{selectedRes.id}</Typography>
                                </Box>
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: 'primary.light' }}><BusinessIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Establecimiento</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>{selectedRes.board?.business?.name}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: 'secondary.light' }}><ScheduleIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Horario</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>{format(new Date(selectedRes.startDate), 'PPP HH:mm', { locale: es })}</Typography>
                                        </Box>
                                    </Box>
                                    {selectedRes.customerPhone && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: 'info.light' }}><PhoneIcon /></Avatar>
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>{selectedRes.customerPhone}</Typography>
                                        </Box>
                                    )}
                                    {selectedRes.customerEmail && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: 'warning.light' }}><EmailIcon /></Avatar>
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>{selectedRes.customerEmail}</Typography>
                                        </Box>
                                    )}
                                    {selectedRes.notes && (
                                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: 'action.hover' }}><NotesIcon /></Avatar>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Notas</Typography>
                                                <Typography variant="body2">{selectedRes.notes}</Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Stack>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                            {selectedRes.status === 'pending' && (
                                <>
                                    <Button
                                        onClick={async () => {
                                            try {
                                                await axios.put(`/api/reservations/${selectedRes.id}`, { status: 'confirmed' });
                                                fetchLiveData();
                                                setSelectedRes(null);
                                                say('¡Turno aprobado! 🎊', 'celebration');
                                            } catch (error) {
                                                alert('Error al aprobar el turno');
                                            }
                                        }}
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircleIcon />}
                                        sx={{ borderRadius: 3, flex: 1, fontWeight: 900 }}
                                    >
                                        Aprobar
                                    </Button>
                                    <Button
                                        onClick={async () => {
                                            if (!window.confirm('¿Estás seguro de que querés rechazar este turno?')) return;
                                            try {
                                                await axios.put(`/api/reservations/${selectedRes.id}`, { status: 'cancelled' });
                                                fetchLiveData();
                                                setSelectedRes(null);
                                                say('Turno rechazado 😔', 'alert');
                                            } catch (error) {
                                                alert('Error al rechazar el turno');
                                            }
                                        }}
                                        variant="outlined"
                                        color="error"
                                        startIcon={<ErrorIcon />}
                                        sx={{ borderRadius: 3, flex: 1, fontWeight: 900 }}
                                    >
                                        Rechazar
                                    </Button>
                                </>
                            )}
                            {selectedRes.status === 'confirmed' && (
                                <Button
                                    onClick={async () => {
                                        if (!window.confirm('¿Querés cancelar este turno ya confirmado?')) return;
                                        try {
                                            await axios.put(`/api/reservations/${selectedRes.id}`, { status: 'cancelled' });
                                            fetchLiveData();
                                            setSelectedRes(null);
                                            say('Turno cancelado 🚫', 'tip');
                                        } catch (error) {
                                            alert('Error al cancelar el turno');
                                        }
                                    }}
                                    variant="outlined"
                                    color="error"
                                    startIcon={<ErrorIcon />}
                                    sx={{ borderRadius: 3, flex: 1, fontWeight: 800 }}
                                >
                                    Cancelar Turno
                                </Button>
                            )}
                            <Button
                                onClick={() => setIsEditing(true)}
                                variant="contained"
                                color="primary"
                                sx={{ borderRadius: 3, flex: 1, fontWeight: 800 }}
                            >
                                Editar
                            </Button>
                            <Button
                                onClick={() => setSelectedRes(null)}
                                variant="outlined"
                                sx={{ borderRadius: 3, flex: { xs: 1, sm: 0.5 }, fontWeight: 800 }}
                            >
                                Cerrar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Edit Modal */}
            <Dialog
                open={isEditing}
                onClose={() => setIsEditing(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 5, p: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, mb: 2 }}>Editar Reserva</DialogTitle>
                <DialogContent>
                    {selectedRes && (
                        <ReservationForm
                            isEditMode={true}
                            boards={boards}
                            initialData={{
                                customerName: selectedRes.customerName,
                                customerEmail: selectedRes.customerEmail,
                                customerPhone: selectedRes.customerPhone,
                                boardId: selectedRes.board?.id.toString(),
                                startDate: new Date(selectedRes.startDate),
                                endDate: new Date(selectedRes.endDate),
                                startTime: new Date(selectedRes.startDate),
                                endTime: new Date(selectedRes.endDate),
                                notes: selectedRes.notes,
                                status: selectedRes.status,
                            }}
                            onSubmit={handleEditSubmit}
                            onCancel={() => setIsEditing(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default LiveDashboard;
